/**
 * M3.1 · Roster Import Wizard (institutional_admin, B2B)
 *
 * Consumes the M3.1 endpoints exclusively:
 *   GET  /api/admin/b2b/roster/template
 *   POST /api/admin/b2b/roster/preview
 *   POST /api/admin/b2b/roster/commit  (multipart)
 *   GET  /api/admin/b2b/roster/imports
 *   POST /api/admin/b2b/roster/imports/{id}/regenerate-credentials
 *
 * Contract highlights honored:
 *   - Preview NEVER writes.
 *   - Commit requires an explicit user click; button disables while busy.
 *   - Temp credentials are only shown by streaming the ephemeral HTML
 *     into a same-tab pop-up window (no persistence, no re-render).
 *   - Fails closed on Buddy Avanza disabled OR missing schools.roster_prefix.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Printer,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  History,
  Download,
} from "lucide-react";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

// eslint-disable-next-line react/prop-types
export default function RosterImportWizard() {
  const [step, setStep] = useState("idle"); // idle | previewing | preview_ready | committing | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const clearError = () => setError(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/admin/b2b/roster/imports?limit=20`, {
        withCredentials: true,
      });
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // 403/401 → hide silently; the tab is guarded by role at the parent level.
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleDownloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/admin/b2b/roster/template`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "buddy_avanza_roster_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string" ? detail : "No se pudo descargar la plantilla.";
      toast.error(msg);
    }
  };

  const handleFileSelect = (ev) => {
    clearError();
    setPreview(null);
    setCommitResult(null);
    setStep("idle");
    const f = ev.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_SIZE) {
      setError("El archivo excede el límite de 5 MiB.");
      setFile(null);
      return;
    }
    if (!/\.csv$/i.test(f.name)) {
      setError("Solo se acepta CSV (UTF-8) por ahora.");
      setFile(null);
      return;
    }
    setFile(f);
  };

  const parseErrorDetail = (e) => {
    const d = e?.response?.data?.detail;
    if (!d) return "Error desconocido.";
    if (typeof d === "string") {
      if (d === "buddy_avanza_disabled_for_tenant") {
        return "Buddy Avanza no está habilitado en tu institución. Contacta a Super Admin.";
      }
      if (d === "school_roster_prefix_missing") {
        return "Tu institución no tiene un prefijo de roster configurado. Contacta a Super Admin para asignarlo antes de importar.";
      }
      if (d === "cross_tenant_denied") return "Operación denegada (cross-tenant).";
      return d;
    }
    if (d.code === "prohibited_columns") {
      return `Columnas prohibidas detectadas: ${(d.columns || []).join(", ")}`;
    }
    return JSON.stringify(d);
  };

  const doPreview = async () => {
    if (!file) return;
    clearError();
    setStep("previewing");
    setPreview(null);
    setCommitResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/b2b/roster/preview`, fd, {
        withCredentials: true,
      });
      setPreview(r.data);
      setStep("preview_ready");
    } catch (e) {
      setError(parseErrorDetail(e));
      setStep("idle");
    }
  };

  const doCommit = async () => {
    if (!preview?.preview_token || !file) return;
    clearError();
    setStep("committing");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("preview_token", preview.preview_token);
    fd.append("format", "json");
    try {
      const r = await axios.post(`${API}/admin/b2b/roster/commit`, fd, {
        withCredentials: true,
      });
      setCommitResult(r.data);
      setStep("done");
      loadHistory();
      toast.success("Importación completada.");
    } catch (e) {
      setError(parseErrorDetail(e));
      setStep("preview_ready");
    }
  };

  const openEnvelope = async () => {
    if (!preview?.preview_token || !file) return;
    // Envelope must be requested with an EXPLICIT format=html; JSON commit
    // above already succeeded, so we need to re-hit commit endpoint with a
    // FRESH preview (idempotent commit will not create new rows).
    // To keep contract simple, we re-run preview → then commit with html.
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/b2b/roster/preview`, fd, {
        withCredentials: true,
      });
      const fd2 = new FormData();
      fd2.append("file", file);
      fd2.append("preview_token", r.data.preview_token);
      fd2.append("format", "html");
      const html = await axios.post(`${API}/admin/b2b/roster/commit`, fd2, {
        withCredentials: true,
        responseType: "text",
        headers: { Accept: "text/html" },
      });
      // Open in a new window with no-store headers (browser tab lifecycle only).
      const w = window.open("", "_blank", "noopener,noreferrer,width=900,height=700");
      if (!w) {
        toast.error(
          "El navegador bloqueó la ventana emergente. Habilita pop-ups para imprimir credenciales."
        );
        return;
      }
      w.document.open();
      w.document.write(html.data);
      w.document.close();
    } catch (e) {
      toast.error(parseErrorDetail(e));
    }
  };

  const regenerateCredentials = async (importId, userIds) => {
    try {
      const r = await axios.post(
        `${API}/admin/b2b/roster/imports/${importId}/regenerate-credentials`,
        { user_ids: userIds },
        { withCredentials: true, responseType: "text" }
      );
      const w = window.open("", "_blank", "noopener,noreferrer,width=900,height=700");
      if (!w) {
        toast.error("Habilita pop-ups para imprimir credenciales.");
        return;
      }
      w.document.open();
      w.document.write(r.data);
      w.document.close();
    } catch (e) {
      toast.error(parseErrorDetail(e));
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCommitResult(null);
    setError(null);
    setStep("idle");
  };

  return (
    <div className="space-y-6" data-testid="m31-roster-wizard">
      <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100">
        <p className="text-sm text-blue-700 font-medium mb-1">
          Importar estudiantes B2B (Buddy Avanza)
        </p>
        <p className="text-xs text-blue-600">
          Sube el roster institucional en formato CSV. Se validará antes de crear cuentas.
          Las contraseñas temporales se muestran una sola vez, se imprimen y se entregan.
          Nunca se almacenan.
        </p>
      </div>

      {error && (
        <div
          className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2"
          data-testid="m31-error-banner"
        >
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* STEP 1: Template + upload */}
      <div className="flex flex-wrap gap-3 items-center">
        <Button
          variant="outline"
          onClick={handleDownloadTemplate}
          data-testid="m31-download-template-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Descargar plantilla CSV
        </Button>
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer hover:bg-gray-50 text-sm">
          <Upload className="w-4 h-4" />
          {file ? file.name : "Seleccionar archivo…"}
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="m31-file-input"
          />
        </label>
        <Button
          onClick={doPreview}
          disabled={!file || step === "previewing"}
          data-testid="m31-validate-btn"
        >
          {step === "previewing" ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Validando…
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              Validar / Preview
            </>
          )}
        </Button>
      </div>

      {/* STEP 2: Preview totals */}
      {preview && (
        <div
          className="rounded-xl border p-4 space-y-3"
          data-testid="m31-preview-panel"
        >
          <h3 className="font-semibold text-gray-900">Resultado de validación</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {Object.entries(preview.totals).map(([k, v]) => (
              <div
                key={k}
                className="p-2 rounded-lg bg-gray-50 text-xs"
                data-testid={`m31-total-${k}`}
              >
                <div className="text-gray-500 uppercase tracking-wide">
                  {k.replaceAll("_", " ")}
                </div>
                <div className="text-lg font-mono text-gray-900">{String(v)}</div>
              </div>
            ))}
          </div>

          {preview.rows?.some((r) => r.status === "error") && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1">Errores por fila:</p>
              <div className="max-h-52 overflow-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 text-gray-600">
                    <tr>
                      <th className="text-left px-2 py-1">Fila</th>
                      <th className="text-left px-2 py-1">Estado</th>
                      <th className="text-left px-2 py-1">Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows
                      .filter((r) => r.status === "error")
                      .map((r) => (
                        <tr key={r.row_number} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.row_number}</td>
                          <td className="px-2 py-1 text-red-600">error</td>
                          <td className="px-2 py-1 font-mono">
                            {(r.issues || []).join(", ")}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={doCommit}
              disabled={
                step === "committing" ||
                !preview.preview_token ||
                (preview.totals.valid_rows || 0) === 0
              }
              data-testid="m31-commit-btn"
            >
              {step === "committing" ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importando…
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirmar importación ({preview.totals.valid_rows} filas)
                </>
              )}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="m31-cancel-btn">
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3: Commit result + envelope */}
      {commitResult && (
        <div
          className="rounded-xl border p-4 space-y-3 bg-green-50/40 border-green-200"
          data-testid="m31-commit-result"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-green-900">
              Importación #{commitResult.import_id} completada
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            {Object.entries(commitResult.totals || {}).map(([k, v]) => (
              <div key={k} className="p-2 rounded bg-white border">
                <div className="text-gray-500 uppercase tracking-wide">
                  {k.replaceAll("_", " ")}
                </div>
                <div className="font-mono text-gray-900">{String(v)}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={openEnvelope}
              data-testid="m31-print-envelope-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir credenciales temporales
            </Button>
            <Button variant="outline" onClick={reset} data-testid="m31-new-import-btn">
              Nueva importación
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Documento efímero. Las contraseñas se muestran una sola vez y no se
            almacenan en base de datos. Si cierras la ventana sin imprimir, usa
            &quot;regenerar credenciales&quot; desde el historial.
          </p>
        </div>
      )}

      {/* Import history */}
      <div className="rounded-xl border p-4" data-testid="m31-history-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-gray-500" />
            <h3 className="font-semibold text-gray-900">Historial de importaciones</h3>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={loadHistory}
            disabled={historyLoading}
            data-testid="m31-history-refresh-btn"
          >
            <RefreshCw
              className={`w-4 h-4 ${historyLoading ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="m31-history-empty">
            Aún no se ha realizado ninguna importación.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
                <tr>
                  <th className="text-left px-2 py-1">Fecha</th>
                  <th className="text-left px-2 py-1">Archivo</th>
                  <th className="text-left px-2 py-1">Estado</th>
                  <th className="text-right px-2 py-1">Total</th>
                  <th className="text-right px-2 py-1">Creados</th>
                  <th className="text-right px-2 py-1">Actualizados</th>
                  <th className="text-right px-2 py-1">Rechazados</th>
                </tr>
              </thead>
              <tbody data-testid="m31-history-list">
                {history.map((h) => {
                  const t = h.totals_commit || h.totals || {};
                  const rejected =
                    (t.errors || 0) +
                    ((t.rows_total || 0) - (t.valid_rows || 0));
                  return (
                    <tr
                      key={h.id}
                      className="border-t"
                      data-testid={`m31-history-row-${h.id}`}
                    >
                      <td className="px-2 py-1 font-mono text-xs">
                        {String(h.uploaded_at || "").slice(0, 19).replace("T", " ")}
                      </td>
                      <td className="px-2 py-1 truncate max-w-[240px]">
                        {h.file_name_sanitized || "—"}
                      </td>
                      <td className="px-2 py-1">
                        <Badge
                          className={
                            h.status === "committed"
                              ? "bg-green-100 text-green-700"
                              : h.status === "committing"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-gray-100 text-gray-700"
                          }
                        >
                          {h.status}
                        </Badge>
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {t.rows_total ?? "—"}
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {t.users_created ?? "—"}
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {t.users_updated ?? "—"}
                      </td>
                      <td className="px-2 py-1 text-right font-mono">
                        {rejected || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
